import { useState, useEffect, useCallback, useRef } from "react";
import {
  initializeDelSocket,
  joinDel,
  leaveDel,
  emitDelPatch,
  onDelConnectionChange,
  onDelUpdate,
  onDelJsaUpdate,
  onDelWasteDisposalUpdate,
  onDelPhotosUpdate,
  clearDelHandlers,
  isDelSocketConnected,
} from "../services/delSocketService";
import {
  queuePatch,
  flushQueue,
  getPendingCount,
} from "../services/delPatchQueue";
import { useAppStore } from "../store/appStore";

/**
 * Hook for managing DEL form WebSocket autosave with error handling
 * @param {string} delId - The DEL ID to connect to
 * @param {Object} autosaveErrorsActions - Optional Zustand actions for error state management
 * @returns {Object} Autosave state and emitPatch function
 */
export const useDelAutosave = (delId, autosaveErrorsActions = null) => {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, saved, queued, error
  const [pendingCount, setPendingCount] = useState(0);
  const [lastError, setLastError] = useState(null); // Stores last error for UI display
  const joinedRef = useRef(false);
  const inflightRef = useRef(0);

  // Initialize socket and join room
  useEffect(() => {
    if (!delId) return;

    initializeDelSocket();

    const handleConnectionChange = async (connected) => {
      setConnectionStatus(connected ? "connected" : "disconnected");
      if (!connected) {
        // Reset joined flag on disconnect so we re-join on reconnect
        joinedRef.current = false;
      }
      if (connected && delId) {
        try {
          if (!joinedRef.current) {
            await joinDel(delId);
            joinedRef.current = true;
          }
          // Register any drafts created offline before flushing their queued
          // patches; flushing against a DEL the server doesn't know would get
          // the patches rejected and permanently dropped from the queue.
          const state = useAppStore.getState();
          const pendingCreates =
            state.activeDelDrafts?.pendingDraftCreates || [];
          if (pendingCreates.length > 0) {
            await state.activeDelDraftsActions?.ensureDraftCreated?.();
            const stillPending =
              useAppStore.getState().activeDelDrafts?.pendingDraftCreates || [];
            if (stillPending.some((p) => String(p.delId) === String(delId))) {
              // This DEL still isn't on the server; keep its patches queued
              return;
            }
          }
          const flushed = await flushQueue(delId, emitDelPatch);
          if (flushed > 0) {
            const remaining = await getPendingCount(delId);
            setPendingCount(remaining);
          }
        } catch (err) {
          console.error("Error during DEL socket reconnection:", err);
        }
      }
    };

    // Handle incoming DEL section broadcasts - route to store's handleBroadcast
    const handleUpdate = (patchData) => {
      try {
        const state = useAppStore.getState();

        // Check which DEL context has this delId and route accordingly
        if (state.activeDelDrafts?.currentDel?.id === delId) {
          if (state.activeDelDraftsActions?.delFormActions?.handleBroadcast) {
            state.activeDelDraftsActions.delFormActions.handleBroadcast(patchData);
            state.activeDelDraftsActions.delFormActions.bump();
          }
        } else if (state.activeDelSubmits?.currentDel?.id === delId) {
          if (state.activeDelSubmitsActions?.delFormActions?.handleBroadcast) {
            state.activeDelSubmitsActions.delFormActions.handleBroadcast(patchData);
            state.activeDelSubmitsActions.delFormActions.bump();
          }
        } else if (state.activeDelApprovalsAwaiting?.currentDel?.id === delId) {
          if (state.activeDelApprovalsAwaitingActions?.delFormActions?.handleBroadcast) {
            state.activeDelApprovalsAwaitingActions.delFormActions.handleBroadcast(patchData);
            state.activeDelApprovalsAwaitingActions.delFormActions.bump();
          }
        }
      } catch (e) {
        console.error("Failed to apply DEL websocket patch:", e);
      }
    };

    // Handle incoming JSA broadcasts - route to JSA handler
    const handleJsaUpdate = (patchData) => {
      try {
        const state = useAppStore.getState();

        // Check which DEL context has this delId and route accordingly
        if (state.activeDelDrafts?.currentDel?.id === delId) {
          if (state.activeDelDraftsActions?.delFormActions?.jsa?.handleJsaBroadcast) {
            state.activeDelDraftsActions.delFormActions.jsa.handleJsaBroadcast(patchData);
            state.activeDelDraftsActions.delFormActions.bump();
          }
        } else if (state.activeDelSubmits?.currentDel?.id === delId) {
          if (state.activeDelSubmitsActions?.delFormActions?.jsa?.handleJsaBroadcast) {
            state.activeDelSubmitsActions.delFormActions.jsa.handleJsaBroadcast(patchData);
            state.activeDelSubmitsActions.delFormActions.bump();
          }
        } else if (state.activeDelApprovalsAwaiting?.currentDel?.id === delId) {
          if (state.activeDelApprovalsAwaitingActions?.delFormActions?.jsa?.handleJsaBroadcast) {
            state.activeDelApprovalsAwaitingActions.delFormActions.jsa.handleJsaBroadcast(patchData);
            state.activeDelApprovalsAwaitingActions.delFormActions.bump();
          }
        }
      } catch (e) {
        console.error("Failed to apply JSA websocket patch:", e);
      }
    };

    // Handle incoming Waste Disposal broadcasts - route to Waste Disposal handler
    const handleWasteDisposalUpdate = (patchData) => {
      try {
        const state = useAppStore.getState();

        // Check which DEL context has this delId and route accordingly
        if (state.activeDelDrafts?.currentDel?.id === delId) {
          if (state.activeDelDraftsActions?.delFormActions?.wasteDisposal?.handleWasteDisposalBroadcast) {
            state.activeDelDraftsActions.delFormActions.wasteDisposal.handleWasteDisposalBroadcast(patchData);
            state.activeDelDraftsActions.delFormActions.bump();
          }
        } else if (state.activeDelSubmits?.currentDel?.id === delId) {
          if (state.activeDelSubmitsActions?.delFormActions?.wasteDisposal?.handleWasteDisposalBroadcast) {
            state.activeDelSubmitsActions.delFormActions.wasteDisposal.handleWasteDisposalBroadcast(patchData);
            state.activeDelSubmitsActions.delFormActions.bump();
          }
        } else if (state.activeDelApprovalsAwaiting?.currentDel?.id === delId) {
          if (state.activeDelApprovalsAwaitingActions?.delFormActions?.wasteDisposal?.handleWasteDisposalBroadcast) {
            state.activeDelApprovalsAwaitingActions.delFormActions.wasteDisposal.handleWasteDisposalBroadcast(patchData);
            state.activeDelApprovalsAwaitingActions.delFormActions.bump();
          }
        }
      } catch (e) {
        console.error("Failed to apply Waste Disposal websocket patch:", e);
      }
    };

    // Handle incoming Photo broadcasts - route to Photos handler
    const handlePhotosUpdate = (patchData) => {
      try {
        const state = useAppStore.getState();

        // Check which DEL context has this delId and route accordingly
        if (state.activeDelDrafts?.currentDel?.id === delId) {
          if (state.activeDelDraftsActions?.delFormActions?.photos?.handleBroadcast) {
            state.activeDelDraftsActions.delFormActions.photos.handleBroadcast(patchData);
            state.activeDelDraftsActions.delFormActions.bump();
          }
        } else if (state.activeDelSubmits?.currentDel?.id === delId) {
          if (state.activeDelSubmitsActions?.delFormActions?.photos?.handleBroadcast) {
            state.activeDelSubmitsActions.delFormActions.photos.handleBroadcast(patchData);
            state.activeDelSubmitsActions.delFormActions.bump();
          }
        } else if (state.activeDelApprovalsAwaiting?.currentDel?.id === delId) {
          if (state.activeDelApprovalsAwaitingActions?.delFormActions?.photos?.handleBroadcast) {
            state.activeDelApprovalsAwaitingActions.delFormActions.photos.handleBroadcast(patchData);
            state.activeDelApprovalsAwaitingActions.delFormActions.bump();
          }
        }
      } catch (e) {
        console.error("Failed to apply Photos websocket patch:", e);
      }
    };

    // Register all handlers
    onDelConnectionChange(handleConnectionChange);
    onDelUpdate(handleUpdate);
    onDelJsaUpdate(handleJsaUpdate);
    onDelWasteDisposalUpdate(handleWasteDisposalUpdate);
    onDelPhotosUpdate(handlePhotosUpdate);

    // Initial join
    joinDel(delId)
      .then(() => {
        joinedRef.current = true;
      })
      .catch((err) => console.error("Failed to join DEL room:", err));

    // Update pending count
    getPendingCount(delId)
      .then(setPendingCount)
      .catch((err) => console.error("Failed to get pending patch count:", err));

    return () => {
      if (delId && joinedRef.current) {
        // Attempt to flush any queued patches before leaving, unless this
        // draft is still awaiting server registration (flushing would get the
        // patches rejected and dropped)
        const stillPendingCreate = (
          useAppStore.getState().activeDelDrafts?.pendingDraftCreates || []
        ).some((p) => String(p.delId) === String(delId));
        if (isDelSocketConnected() && !stillPendingCreate) {
          flushQueue(delId, emitDelPatch)
            .then(() => leaveDel(delId))
            .catch(() => leaveDel(delId).catch((err) =>
              console.warn("Failed to leave DEL room after flush error:", err)
            ));
        } else {
          leaveDel(delId).catch((err) =>
            console.warn("Failed to leave DEL room (socket disconnected):", err)
          );
        }
        joinedRef.current = false;
      }
      clearDelHandlers();
    };
  }, [delId]);

  // Emit patch with queue fallback and structured error handling
  // Returns: { success: boolean, error?: string, data?: object }
  const emitPatch = useCallback(
    async (patch, options = {}) => {
      const { onError, onSuccess } = options;

      if (!delId) return { success: false, error: "No DEL ID" };

      // Draft created offline and not yet registered on the server: queue the
      // patch instead of emitting it, since the server would reject a patch
      // for an unknown DEL and rejected patches are dropped permanently.
      const pendingCreates =
        useAppStore.getState().activeDelDrafts?.pendingDraftCreates || [];
      if (pendingCreates.some((p) => String(p.delId) === String(delId))) {
        try {
          await queuePatch(delId, patch);
          const count = await getPendingCount(delId);
          setPendingCount(count);
        } catch (idbErr) {
          console.warn(
            "Failed to queue DEL patch to browser storage. Your changes may not be saved offline. Try refreshing the page if this persists.",
            idbErr?.message || idbErr
          );
        }
        setSaveStatus("queued");
        // If we do have a connection, try to register the draft now and flush
        // the queue so the user's work reaches the server without waiting for
        // the next reconnect event
        if (isDelSocketConnected()) {
          const actions = useAppStore.getState().activeDelDraftsActions;
          actions
            ?.ensureDraftCreated?.(delId)
            .then(async (res) => {
              if (res?.ok) {
                await flushQueue(delId, emitDelPatch);
                const remaining = await getPendingCount(delId);
                setPendingCount(remaining);
              }
            })
            .catch(() => {});
        }
        return { success: false, error: null, queued: true };
      }

      inflightRef.current += 1;
      setSaveStatus("saving");

      const result = await emitDelPatch(delId, patch);

      inflightRef.current -= 1;

      if (!result.error && !result.shouldQueue) {
        // Success - clear any previous errors for this patch type
        setSaveStatus("saved");
        setLastError(null);
        setTimeout(() => setSaveStatus("idle"), 2000);

        // If server corrected the JSA ID (frontend had stale/wrong ID),
        // update the local JSA model so future patches use the correct ID
        if (result.data?.corrected_jsa_id) {
          const state = useAppStore.getState();
          const contexts = [
            state.activeDelDrafts,
            state.activeDelSubmits,
            state.activeDelApprovalsAwaiting,
          ];
          for (const ctx of contexts) {
            if (ctx?.currentDel?.id === delId && ctx?.currentDel?.jsa?.jsaBasicInfo) {
              ctx.currentDel.jsa.jsaBasicInfo.rowId = result.data.corrected_jsa_id;
              console.warn(
                "JSA ID corrected by server:",
                result.data.corrected_jsa_id
              );
              break;
            }
          }
        }

        // Get the actual patch type for error clearing (handle nested patches)
        // For JSA and Waste Disposal, the inner patch_type is used for error keying
        let patchTypeForClearing = patch.patch_type;
        let rowIdForClearing = patch.id;

        if (patch.patch_type === "jsa" && patch.jsa_data?.patch_type) {
          patchTypeForClearing = patch.jsa_data.patch_type;
          rowIdForClearing = patch.jsa_data.id || rowIdForClearing;
        } else if (patch.patch_type === "waste_disposal" && patch.waste_disposal_data?.patch_type) {
          patchTypeForClearing = patch.waste_disposal_data.patch_type;
          rowIdForClearing = patch.waste_disposal_data.id || rowIdForClearing;
        }

        // Clear field errors for successful save
        if (patchTypeForClearing && autosaveErrorsActions) {
          autosaveErrorsActions.clearPatchTypeErrors(patchTypeForClearing);
        }

        // Clear row error if this was a row operation
        if (rowIdForClearing && autosaveErrorsActions) {
          autosaveErrorsActions.clearRowError(rowIdForClearing);
        }

        onSuccess?.(result.data);
        return { success: true, data: result.data };
      }

      if (result.shouldQueue) {
        // Network issue (disconnected, timeout, no response) - queue for retry
        try {
          await queuePatch(delId, patch);
          const count = await getPendingCount(delId);
          setPendingCount(count);
        } catch (idbErr) {
          console.warn(
            "Failed to queue DEL patch to browser storage. Your changes may not be saved offline. Try refreshing the page if this persists.",
            idbErr?.message || idbErr
          );
        }
        setSaveStatus("queued");
      } else {
        // Server validation/database error - parse and set field errors
        setSaveStatus("error");

        // Parse structured error response from server
        const errorData = result.data || {};
        setLastError(errorData);

        // Use autosaveErrorsActions if available to set field-level errors
        if (autosaveErrorsActions && errorData.error_type) {
          autosaveErrorsActions.handleServerError(errorData);
        }

        // Log for debugging
        console.error("DEL patch error:", errorData);

        // Handle unauthorized - redirect to login
        if (result.error === "Unauthorized") {
          window.location.href = "/login";
        }

        onError?.(errorData);

        // Reset to idle after showing error briefly
        setTimeout(() => setSaveStatus("idle"), 3000);
      }

      return { success: false, error: result.error, data: result.data };
    },
    [delId, autosaveErrorsActions]
  );

  const hasPendingPatches = useCallback(() => {
    return inflightRef.current > 0;
  }, []);

  return {
    emitPatch,
    connectionStatus,
    saveStatus,
    pendingCount,
    lastError,
    hasPendingPatches,
  };
};
